/**
 * Room detail · Lessor & Bank tab.
 *
 * Read-only display of the effective representor + bank account at the
 * room level, with inheritance badges showing whether the value comes
 * from the room override, property override, or lessor default.
 *
 * Editing room-level overrides is a small follow-up — for B5 this is
 * read-only and the editor uses the legacy admin if they need to
 * override a single room.
 */
import { Pencil } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InheritanceBadge } from "@/components/admin/v2/InheritanceBadge";
import type { EffectiveBank, EffectiveRep } from "@/types/property";
import { maskIban } from "@/lib/v2-format";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  effectiveRep: EffectiveRep;
  effectiveBank: EffectiveBank;
  /** Opens the room editor (representor + bank overrides live there). */
  onEdit?: () => void;
};

export function RoomLessorBankTab({
  effectiveRep,
  effectiveBank,
  onEdit,
}: Props) {
  const { t } = useLanguage();
  return (
    <div className="space-y-4">
      {onEdit && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {t(
              "admin.rooms.valuesInheritFromLessor",
              "Values inherit from the lessor unless overridden at property or room level."
            )}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" />
            {t("common.edit", "Edit")}
          </Button>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Representor */}
        <Card className="p-5 space-y-3">
          <header className="flex items-center justify-between gap-3">
            <div className="space-y-0.5">
              <h2 className="text-base font-semibold">
                {t("admin.field.representor", "Representor")}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t(
                  "admin.rooms.signsContractsForRoom",
                  "Signs contracts for this room."
                )}
              </p>
            </div>
            <InheritanceBadge source={effectiveRep.source} level="room" />
          </header>
          {effectiveRep.name ? (
            <div className="text-sm space-y-1">
              <p className="font-medium">{effectiveRep.name}</p>
              {effectiveRep.note && (
                <p className="text-xs italic text-muted-foreground">
                  {effectiveRep.note}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("admin.field.noRepConfigured", "No representor configured.")}
            </p>
          )}
        </Card>

        {/* Bank */}
        <Card className="p-5 space-y-3">
          <header className="flex items-center justify-between gap-3">
            <div className="space-y-0.5">
              <h2 className="text-base font-semibold">
                {t("admin.bulkEdit.bankAccount", "Bank account")}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t(
                  "admin.rooms.whereRentIsSent",
                  "Where rent for this room is sent."
                )}
              </p>
            </div>
            <InheritanceBadge source={effectiveBank.source} level="room" />
          </header>
          {effectiveBank.iban ? (
            <dl className="space-y-2 text-sm">
              <Row
                label={t("admin.field.holder", "Holder")}
                value={effectiveBank.holder ?? "—"}
              />
              <Row
                label={t("admin.field.bankIban", "IBAN")}
                value={maskIban(effectiveBank.iban)}
                mono
              />
              {effectiveBank.bic && (
                <Row
                  label={t("admin.field.bankBic", "BIC")}
                  value={effectiveBank.bic}
                  mono
                />
              )}
              {effectiveBank.bank_name && (
                <Row
                  label={t("admin.field.bankShort", "Bank")}
                  value={effectiveBank.bank_name}
                />
              )}
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("admin.field.noBankConfigured", "No bank account configured.")}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`font-medium ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </dd>
    </div>
  );
}
